import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Input, Modal, Select, Spinner } from '@/components/ui';
import {
  archivePolicy,
  createPolicyDraft,
  getPolicy,
  POLICY_CATEGORY_LABELS,
  withdrawPolicy,
  type PolicyContext,
} from '@/api/services/hrPolicies.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { POLICY_STATUS, POLICY_VERSION_STATUS } from './policyFormat';
import { PolicyVersionPanel } from './PolicyVersionPanel';
import { PolicyAudiencePanel } from './PolicyAudiencePanel';
import { PolicyReportPanel } from './PolicyReportPanel';

type View = 'content' | 'audience' | 'report';

const SEGMENT_CLASS =
  'rounded-md px-3 py-1.5 text-sm transition-colors aria-selected:bg-primary/10 aria-selected:text-primary hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:aria-selected:text-sky-400';

/**
 * One policy: its versions and content, who it applies to, and who has
 * acknowledged it. Company HR opening an organization policy sees it
 * read-only, with report rows for its own company.
 */
export function PolicyDialog({
  context,
  policyId,
  onClose,
}: {
  context: PolicyContext;
  policyId: string;
  onClose: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const contextKey = context.companyId ?? 'organization';
  const [view, setView] = useState<View>('content');
  const [versionId, setVersionId] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['hr-policy', contextKey, policyId],
    queryFn: () => getPolicy(context, policyId),
  });
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hr-policy', contextKey, policyId] });
    void queryClient.invalidateQueries({ queryKey: ['hr-policies'] });
    void queryClient.invalidateQueries({ queryKey: ['hr-policy-report'] });
  };
  const fail = (mutationError: unknown): void => setError(getErrorMessage(mutationError));

  const versions = data?.versions ?? [];
  const openVersion = versions.find(
    (row) => row.status === 'draft' || row.status === 'pending_approval' || row.status === 'approved'
  );
  const current = versions.find((row) => row._id === data?.policy.currentVersionId);
  const selectedId = versionId ?? openVersion?._id ?? current?._id ?? versions[0]?._id ?? null;
  const reportVersion =
    versions.find((row) => row._id === selectedId && row.publishedAt) ?? current ?? null;

  const newDraft = useMutation({
    mutationFn: () => createPolicyDraft(context, policyId),
    onSuccess: (created) => {
      setVersionId(created._id);
      setView('content');
      refresh();
    },
    onError: fail,
  });
  const withdraw = useMutation({
    mutationFn: () => withdrawPolicy(context, policyId, reason.trim()),
    onSuccess: () => {
      toast.success('Policy withdrawn. Outstanding requests were cancelled.');
      setWithdrawing(false);
      refresh();
    },
    onError: fail,
  });
  const archive = useMutation({
    mutationFn: () => archivePolicy(context, policyId),
    onSuccess: () => {
      toast.success('Policy archived');
      refresh();
      onClose();
    },
    onError: fail,
  });

  let body: React.ReactNode;
  if (isLoading || !data) {
    body = (
      <div className="flex justify-center py-10" role="status">
        <Spinner />
        <span className="sr-only">Loading policy</span>
      </div>
    );
  } else {
    const { policy, editable } = data;
    const active = policy.status === 'active';
    const status = POLICY_STATUS[policy.status];
    body = (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={status.variant}>{status.label}</Badge>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {POLICY_CATEGORY_LABELS[policy.category]}
            {policy.ownerCompanyId ? '' : ' · Organization-wide'}
            {!editable && ' · read only'}
          </span>
          {versions.length > 1 && view === 'content' && (
            <div className="ml-auto w-56">
              <Select
                clearable={false}
                label="Version"
                value={selectedId ?? ''}
                onChange={setVersionId}
                options={versions.map((row) => ({
                  value: row._id,
                  label: `v${row.version} · ${POLICY_VERSION_STATUS[row.status].label}`,
                }))}
              />
            </div>
          )}
        </div>
        {policy.withdrawReason && (
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Withdrawn: {policy.withdrawReason}
          </p>
        )}

        <div
          role="tablist"
          aria-label="Policy sections"
          className="inline-flex gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800"
        >
          {(
            [
              ['content', 'Content'],
              ['audience', 'Audience'],
              ['report', 'Acknowledgements'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={view === value}
              className={SEGMENT_CLASS}
              onClick={() => setView(value)}
            >
              {label}
            </button>
          ))}
        </div>

        {view === 'content' && selectedId && (
          <PolicyVersionPanel
            key={selectedId}
            context={context}
            versionId={selectedId}
            editable={editable && active}
            onChanged={refresh}
          />
        )}
        {view === 'audience' && (
          <PolicyAudiencePanel
            key={policy._id}
            context={context}
            policy={policy}
            editable={editable && active}
            onChanged={refresh}
          />
        )}
        {view === 'report' &&
          (reportVersion ? (
            <PolicyReportPanel
              key={reportVersion._id}
              context={context}
              versionId={reportVersion._id}
              canExempt={active}
            />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Acknowledgements appear once a version is published.
            </p>
          ))}

        {withdrawing && (
          <form
            className="space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700"
            onSubmit={(event) => {
              event.preventDefault();
              if (reason.trim().length >= 3) withdraw.mutate();
            }}
          >
            <Input
              id="withdraw-reason"
              label="Why is this policy being withdrawn?"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <p className="text-xs text-gray-600 dark:text-gray-300">
              Employees are no longer asked to acknowledge it. Acknowledgements already given are
              kept as evidence.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setWithdrawing(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="danger"
                size="sm"
                disabled={reason.trim().length < 3}
                loading={withdraw.isPending}
              >
                Withdraw policy
              </Button>
            </div>
          </form>
        )}
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  const policy = data?.policy;
  const editable = data?.editable === true;
  const footer =
    policy && editable ? (
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {policy.status === 'active' && current && !withdrawing && (
            <Button variant="ghost" size="sm" onClick={() => setWithdrawing(true)}>
              Withdraw
            </Button>
          )}
          {policy.status !== 'archived' && (policy.status === 'withdrawn' || !current) && (
            <Button
              variant="ghost"
              size="sm"
              loading={archive.isPending}
              onClick={() => archive.mutate()}
            >
              Archive
            </Button>
          )}
        </div>
        {policy.status === 'active' && !openVersion && versions.length > 0 && (
          <Button size="sm" loading={newDraft.isPending} onClick={() => newDraft.mutate()}>
            Start a new version
          </Button>
        )}
      </div>
    ) : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={policy?.name ?? 'Policy'}
      className="max-w-4xl"
      footer={footer}
    >
      {body}
    </Modal>
  );
}
